import React from "react";
import { Container, Row, Col, Card, CardBody, Button, Label, Input, FormFeedback, Table } from "reactstrap";
import { withTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import Snackbars from "../../Utils/Snackbars";
import useRequests from '../../Utils/useRequests';

const PhoneValidation = props => {
  document.title = 'Phone Number Validation Settings';
  const { getRequest, postRequest } = useRequests();

  const [AlertData, setAlertData] = React.useState({ variant: '', message: '' });
  const resetAlertData = () => {
    setTimeout(() => setAlertData({ variant: '', message: '' }), 5000);
  };

  const [Countries, setCountries] = React.useState([]);
  const [EditingCountry, setEditingCountry] = React.useState(null);
  const [SearchTerm, setSearchTerm] = React.useState('');

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: EditingCountry?.id || '',
      country_name: EditingCountry?.country_name || '',
      country_code: EditingCountry?.phone_code || '',
      min_phone_length: EditingCountry?.min_phone_length || '',
      max_phone_length: EditingCountry?.max_phone_length || '',
    },
    validationSchema: Yup.object({
      min_phone_length: Yup.number()
        .min(1, 'Minimum length must be at least 1')
        .max(20, 'Minimum length cannot exceed 20')
        .required('Minimum length is required'),
      max_phone_length: Yup.number()
        .min(1, 'Maximum length must be at least 1')
        .max(20, 'Maximum length cannot exceed 20')
        .required('Maximum length is required')
        .test('max-greater-than-min', 'Maximum must be greater than or equal to minimum', function(value) {
          const { min_phone_length } = this.parent;
          return value >= min_phone_length;
        }),
    }),
    onSubmit: async (values) => {
      try {
        console.log('Form values before submit:', values);
        
        let submitBtn = document.getElementById('submitBtn');
        submitBtn.disabled = true;
        submitBtn.textContent = 'Updating...';

        const payload = {
          id: values.id,
          min_phone_length: parseInt(values.min_phone_length),
          max_phone_length: parseInt(values.max_phone_length),
        };
        
        console.log('Payload to send:', payload);
        
        const jsonpar = JSON.stringify(payload);

        const res = await postRequest('update-phone-validation-settings', jsonpar);
        
        console.log('API Response:', res);
        
        if (res && res.code === 200) {
          setAlertData({ variant: 'success', message: res.message });
          setEditingCountry(null);
          handleLoadCountries();
        } else {
          setAlertData({ variant: 'error', message: res.message });
        }
        resetAlertData();
        submitBtn.disabled = false;
        submitBtn.textContent = 'Update';
      } catch (error) {
        console.error("Error : ", error);
      }
    }
  });

  const handleLoadCountries = async () => {
    const res = await getRequest('get-phone-validation-settings');
    if (res && res.code === 200) {
      setCountries(res.data);
    } else {
      setAlertData({ variant: 'error', message: res.message });
      resetAlertData();
    }
  };

  const handleEdit = (country) => {
    setEditingCountry(country);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    setEditingCountry(null);
    validation.resetForm();
  };

  React.useEffect(() => {
    handleLoadCountries();
  }, []);

  // Filter countries based on search term
  const filteredCountries = Countries.filter(country => 
    country.country_name.toLowerCase().includes(SearchTerm.toLowerCase()) ||
    country.phone_code.toLowerCase().includes(SearchTerm.toLowerCase())
  );

  return (
    <React.Fragment>
      {AlertData.message !== '' ? <Snackbars variant={AlertData.variant} message={AlertData.message} /> : ''}
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={props.t("Site Settings")} breadcrumbItem={props.t("Phone Number Validation")} />

          {/* Edit Form */}
          {EditingCountry && (
            <Row>
              <Col lg="12">
                <Card className="border-primary">
                  <CardBody>
                    <h4 className="card-title mb-4">
                      Edit Phone Validation for {EditingCountry.country_name}
                    </h4>
                    <Row>
                      <Col md={3}>
                        <div className="mb-3">
                          <Label className="form-label">Country</Label>
                          <Input
                            type="text"
                            value={validation.values.country_name}
                            disabled
                            style={{ backgroundColor: '#f5f5f5' }}
                          />
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="mb-3">
                          <Label className="form-label">Country Code</Label>
                          <Input
                            type="text"
                            value={validation.values.country_code}
                            disabled
                            style={{ backgroundColor: '#f5f5f5' }}
                          />
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="mb-3">
                          <Label className="form-label">Min Phone Length *</Label>
                          <Input
                            name="min_phone_length"
                            type="number"
                            placeholder="e.g., 9"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.min_phone_length || ""}
                            invalid={validation.touched.min_phone_length && validation.errors.min_phone_length ? true : false}
                          />
                          {validation.touched.min_phone_length && validation.errors.min_phone_length ? (
                            <FormFeedback type="invalid">{validation.errors.min_phone_length}</FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="mb-3">
                          <Label className="form-label">Max Phone Length *</Label>
                          <Input
                            name="max_phone_length"
                            type="number"
                            placeholder="e.g., 15"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.max_phone_length || ""}
                            invalid={validation.touched.max_phone_length && validation.errors.max_phone_length ? true : false}
                          />
                          {validation.touched.max_phone_length && validation.errors.max_phone_length ? (
                            <FormFeedback type="invalid">{validation.errors.max_phone_length}</FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                    </Row>
                    <div className="text-left">
                      <Button
                        type="button"
                        color="primary"
                        id="submitBtn"
                        onClick={(e) => { e.preventDefault(); validation.handleSubmit(); }}
                        className="me-2"
                      >
                        Update
                      </Button>
                      <Button
                        type="button"
                        color="secondary"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Countries List */}
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-0">Phone Validation Settings by Country</h4>
                    <div style={{ maxWidth: '300px' }}>
                      <Input
                        type="text"
                        placeholder="Search country or code..."
                        value={SearchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="table-responsive">
                    <Table className="table table-bordered mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Country</th>
                          <th>Country Code</th>
                          <th>Min Phone Length</th>
                          <th>Max Phone Length</th>
                          <th>Example</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCountries.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center">No countries found</td>
                          </tr>
                        ) : (
                          filteredCountries.map((country, index) => (
                            <tr key={index}>
                              <td>{country.country_name}</td>
                              <td>{country.phone_code}</td>
                              <td>{country.min_phone_length || 'Not set'}</td>
                              <td>{country.max_phone_length || 'Not set'}</td>
                              <td className="text-muted">
                                {country.min_phone_length && country.max_phone_length ? (
                                  country.min_phone_length === country.max_phone_length ? (
                                    `${country.min_phone_length} digits`
                                  ) : (
                                    `${country.min_phone_length}-${country.max_phone_length} digits`
                                  )
                                ) : (
                                  'No validation set'
                                )}
                              </td>
                              <td className="text-center">
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleEdit(country)}
                                >
                                  <i className="mdi mdi-pencil"></i> Edit
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>

                  <div className="mt-3 text-muted">
                    <small>
                      <i className="mdi mdi-information"></i> Note: Phone length validation is applied during user registration (excluding country code).
                    </small>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(PhoneValidation);
